const DB_NAME = 'OneClickDatabase';
const STORE_NAME = 'OneClickStore';
const DB_VERSION = 1;

export class Database {
  private db: IDBDatabase | null = null;

  constructor() {
  }

  public init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);

      request.onerror = () => {
        console.error('Error opening database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        if(window.location.hostname.includes('localhost'))
          console.log('Database opened');
        // Check for localStorage "DatabaseVersion"
        const versionStr = window.localStorage.getItem('DatabaseVersion');
        if (versionStr !== null) {
          const storedVersion = parseInt(versionStr, 10);
          if (!isNaN(storedVersion) && storedVersion != DB_VERSION) {
            // Purge all records if version is outdated
            const store = this.getStore('readwrite');
            if (store) {
              const clearRequest = store.clear();
              clearRequest.onsuccess = () => {
                if(window.location.hostname.includes('localhost'))
                  console.log('Cache PURGE ALL');
              };
              clearRequest.onerror = () => {
                console.error('Error purging records on DB version upgrade:', clearRequest.error);
              };
            }
          }
        }
        // Always save the current DB_VERSION
        window.localStorage.setItem('DatabaseVersion', DB_VERSION.toString());
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          if(window.location.hostname.includes('localhost'))
            console.log('Store created');
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
        else {
          if(window.location.hostname.includes('localhost'))
            console.log('Store initialized');
        }
      };
    });
  }

  private getStore(mode: 'readonly' | 'readwrite'): IDBObjectStore | null {
    if (!this.db) return null;
    try {
      const tx = this.db.transaction(STORE_NAME, mode);
      return tx.objectStore(STORE_NAME);
    } catch (e) {
      console.error('Error getting object store:', e);
      return null;
    }
  }

  async putItem(key: string, data: any, update:boolean = false): Promise<void> {
    let date = new Date().toISOString();  

    if(update) {
      let itemDate = await this.getItemDate(key);
      if(itemDate) 
        date = itemDate;
    }

    const store = this.getStore('readwrite');
    if (!store) return;

    const request = store.put({key, date, data});

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        if(window.location.hostname.includes('localhost'))
          console.log('Cache PUT', key);
        resolve();
      };

      request.onerror = () => {
        console.error('Error saving to cache:', request.error);
        reject(request.error);
      };
    });
  }

  async getItem(key: string, expiration:number = 0): Promise<any | null> {
    const store = this.getStore('readonly');
    if (!store) return null;

    const request = store.get(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const cache = request.result;
        if (!cache) {
          resolve(null);
          return;
        }

        let ms = expiration * 1000;
        if (ms > 0 && new Date().getTime() - new Date(cache.date).getTime() > ms) {
          this.removeItem(key)
            .then(() => resolve(null))
            .catch(reject);
          return;
        }

        resolve(cache.data);
      };

      request.onerror = () => {
        console.error('Error getting from cache:', request.error);
        reject(request.error);
      };
    });
  }

  async getItemDate(key: string): Promise<any | null> {
    const store = this.getStore('readonly');
    if (!store) return null;

    const request = store.get(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const cache = request.result;
        if (!cache) {
          resolve(null);
          return;
        }

        resolve(cache.date);
      };

      request.onerror = () => {
        console.error('Error getting from cache:', request.error);
        reject(request.error);
      };
    });
  }

  async removeItem(key: string): Promise<void> {
    const store = this.getStore('readwrite');
    if (!store) return;

    const request = store.delete(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        if(window.location.hostname.includes('localhost'))
          console.log('Cache REMOVE', key);
        resolve();
      };
      request.onerror = () => {
        console.error('Error removing from cache:', request.error);
        reject(request.error);
      };
    });
  }

  async query(key: string, expiration:number = 0): Promise<any[]> {
    const store = this.getStore('readonly');
    if (!store) return [];

    const request = store.openCursor();
    const results: any[] = [];

    return new Promise((resolve, reject) => {
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.key.toString().startsWith(key)) {
            if (expiration > 0) {
              if(new Date().getTime() - new Date(cursor.value.date).getTime() <= expiration) 
                results.push(cursor.value.data);
            } 
            else 
              results.push(cursor.value.data);
          }
          cursor.continue();
        } 
        else 
          resolve(results);
      };

      request.onerror = () => {
        console.error('Error querying cache:', request.error);
        reject(request.error);
      };
    });
  }

  async purge(key: string, expiration: number = 0): Promise<void> {
    const store = this.getStore('readwrite');
    if (!store) return;

    const request = store.openCursor();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.key.toString().startsWith(key)) {
            const itemDate = new Date(cursor.value.date);
            const now = new Date();
            
            let ms = expiration * 1000;
            let age = now.getTime() - itemDate.getTime();
            if (age > ms) {
              if(window.location.hostname.includes('localhost'))
                console.log('Cache PURGE', key);
              cursor.delete();
            }
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        console.error('Error purging cache:', request.error);
        reject(request.error);
      };
    });
  }

  async purgeAll(): Promise<void> {
    const store = this.getStore('readwrite');
    if (!store) return;

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        if(window.location.hostname.includes('localhost'))
          console.log('Cache PURGE ALL');
        resolve();
      };
      request.onerror = () => {
        console.error('Error purging all records:', request.error);
        reject(request.error);
      };
    });
  }
} 